<template>
  <article class="plus-tv-square-card" :class="cardClasses">
    <div
      class="plus-tv-square-card__media relative overflow-hidden"
      :class="item.thumb || item.videoUrl ? '' : ['bg-gradient-to-br', item.gradientCls]"
      @mouseenter="onMediaEnter"
      @mouseleave="onMediaLeave"
    >
      <video
        v-if="item.videoUrl"
        ref="mediaVideo"
        class="plus-tv-square-card__video absolute inset-0 h-full w-full object-cover"
        :class="isHovering ? 'opacity-100' : 'opacity-0'"
        :src="item.videoUrl"
        :poster="item.thumb || undefined"
        :muted="muted"
        loop
        playsinline
        webkit-playsinline
        preload="metadata"
      ></video>
      <img
        v-if="item.thumb"
        :src="item.thumb"
        alt=""
        class="plus-tv-square-card__poster absolute inset-0 h-full w-full object-cover transition-opacity duration-200"
        :class="isHovering && item.videoUrl ? 'opacity-0' : 'opacity-100'"
        loading="lazy"
      />

      <span
        v-if="item.tagLabel"
        class="absolute left-2.5 top-2.5 z-10 rounded-full bg-black/55 px-2.5 py-0.5 text-[11px] font-medium text-white/95 backdrop-blur-sm"
      >
        {{ item.tagLabel }}
      </span>

      <div
        v-if="item.videoUrl"
        class="plus-tv-square-card__controls absolute right-2 top-2 z-10 flex items-center gap-1.5"
        :class="isHovering ? 'plus-tv-square-card__controls--visible' : ''"
      >
        <button
          type="button"
          class="plus-tv-square-card__icon-btn"
          :title="muted ? '开启声音' : '静音'"
          @click.stop="toggleMute"
        >
          <i :class="muted ? 'fas fa-volume-mute' : 'fas fa-volume-up'"></i>
        </button>
        <button
          type="button"
          class="plus-tv-square-card__icon-btn"
          title="全屏"
          @click.stop="onFullscreen"
        >
          <i class="fas fa-up-right-and-down-left-from-center text-[10px]"></i>
        </button>
      </div>

      <div v-if="isFeatured" class="plus-tv-square-card__featured-overlay">
        <div class="min-w-0 flex-1">
          <h3 class="text-[18px] font-bold leading-snug text-white sm:text-[22px]">
            {{ item.title || 'Plus 1.0' }}
          </h3>
          <p v-if="featuredDesc" class="mt-1.5 max-w-md text-[12px] leading-relaxed text-white/65 sm:text-[13px]">
            {{ featuredDesc }}
          </p>
        </div>
        <div
          v-if="pageTotal > 1"
          class="flex shrink-0 items-center gap-1.5 self-end pb-0.5"
          aria-hidden="true"
        >
          <span
            v-for="n in pageTotal"
            :key="'dot-' + n"
            class="h-1.5 w-1.5 rounded-full transition-colors"
            :class="n === currentPage ? 'bg-white' : 'bg-white/35'"
          ></span>
        </div>
      </div>
    </div>

    <div v-if="!isFeatured" class="plus-tv-square-card__meta">
      <div class="plus-tv-square-card__meta-head">
        <img
          v-if="item.userAvatar"
          :src="item.userAvatar"
          alt=""
          class="plus-tv-square-card__meta-avatar"
        />
        <span v-else class="plus-tv-square-card__meta-avatar plus-tv-square-card__meta-avatar--placeholder">
          <i class="fas fa-user"></i>
        </span>
        <p class="plus-tv-square-card__meta-name">{{ item.userLabel || '用户' }}</p>
      </div>
      <p v-if="metaSubtitle" class="plus-tv-square-card__meta-subtitle">{{ metaSubtitle }}</p>
    </div>
  </article>
</template>

<script>
export default {
  name: 'PlusTvSquareCard',
  props: {
    item: { type: Object, required: true },
    variant: {
      type: String,
      default: 'compact',
      validator: (v) => ['compact', 'featured'].includes(v)
    },
    pageTotal: { type: Number, default: 1 },
    currentPage: { type: Number, default: 1 },
    /** 首行左侧大卡与右侧小卡（封面+信息区）等高 */
    heroRowAlign: { type: Boolean, default: false }
  },
  data() {
    return {
      isHovering: false,
      muted: true
    }
  },
  computed: {
    isFeatured() {
      return this.variant === 'featured'
    },
    featuredDesc() {
      return this.item.desc || this.item.subtitle || ''
    },
    metaSubtitle() {
      return this.item.title || this.item.desc || ''
    },
    cardClasses() {
      return {
        'plus-tv-square-card--featured': this.isFeatured,
        'plus-tv-square-card--compact': !this.isFeatured,
        'plus-tv-square-card--hero-align': this.isFeatured && this.heroRowAlign,
        'plus-tv-square-card--placeholder': this.item.placeholder
      }
    }
  },
  beforeDestroy() {
    this.stopVideo()
  },
  methods: {
    getVideoEl() {
      return this.$refs.mediaVideo
    },
    async onMediaEnter() {
      if (this.item.placeholder || !this.item.videoUrl) return
      this.isHovering = true
      this.muted = false
      await this.$nextTick()
      const v = this.getVideoEl()
      if (!v) return
      v.muted = false
      try {
        await v.play()
      } catch (e) {
        this.muted = true
        v.muted = true
        try {
          await v.play()
        } catch (e2) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[plus-tv-card] play failed', e2)
          }
        }
      }
    },
    onMediaLeave() {
      this.isHovering = false
      this.muted = true
      this.stopVideo()
    },
    stopVideo() {
      const v = this.getVideoEl()
      if (!v) return
      v.pause()
      try {
        v.currentTime = 0
      } catch (e) {
        /* ignore */
      }
    },
    toggleMute() {
      this.muted = !this.muted
      const v = this.getVideoEl()
      if (v) v.muted = this.muted
    },
    onFullscreen() {
      const v = this.getVideoEl()
      if (v && v.requestFullscreen) {
        v.requestFullscreen().catch(() => this.openVideoUrl())
        return
      }
      this.openVideoUrl()
    },
    openVideoUrl() {
      if (!this.item.videoUrl) return
      window.open(this.item.videoUrl, '_blank', 'noopener,noreferrer')
    }
  }
}
</script>

<style scoped>
.plus-tv-square-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  text-align: left;
  transition: opacity 0.2s ease;
}
.plus-tv-square-card--placeholder {
  opacity: 0.55;
  pointer-events: none;
}

.plus-tv-square-card--featured {
  grid-column: span 2;
}

.plus-tv-square-card--hero-align {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.plus-tv-square-card--hero-align .plus-tv-square-card__media {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  aspect-ratio: unset;
}

.plus-tv-square-card__media {
  position: relative;
  width: 100%;
  background: #141418;
}

.plus-tv-square-card--compact .plus-tv-square-card__media {
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  flex-shrink: 0;
}

.plus-tv-square-card--featured:not(.plus-tv-square-card--hero-align) .plus-tv-square-card__media {
  aspect-ratio: 16 / 9;
  border-radius: 12px;
}

.plus-tv-square-card--hero-align .plus-tv-square-card__media {
  border-radius: 12px;
}

.plus-tv-square-card__video {
  transition: opacity 0.25s ease;
  z-index: 1;
}
.plus-tv-square-card__poster {
  z-index: 0;
}

.plus-tv-square-card__featured-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 18px;
  background: linear-gradient(
    180deg,
    transparent 35%,
    rgba(0, 0, 0, 0.55) 72%,
    rgba(0, 0, 0, 0.82) 100%
  );
  pointer-events: none;
}

.plus-tv-square-card__controls {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
.plus-tv-square-card__controls--visible {
  opacity: 1;
  pointer-events: auto;
}

.plus-tv-square-card__icon-btn {
  display: flex;
  height: 28px;
  width: 28px;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.52);
  color: rgba(255, 255, 255, 0.92);
  font-size: 11px;
  cursor: pointer;
  backdrop-filter: blur(6px);
  transition: background 0.15s ease;
}
.plus-tv-square-card__icon-btn:hover {
  background: rgba(0, 0, 0, 0.72);
}

.plus-tv-square-card__meta {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 4px;
  min-height: 48px;
  padding: 8px 0 0;
  box-sizing: border-box;
}

.plus-tv-square-card__meta-head {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.plus-tv-square-card__meta-avatar {
  height: 15px;
  width: 15px;
  flex-shrink: 0;
  border-radius: 9999px;
  object-fit: cover;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1);
}

.plus-tv-square-card__meta-avatar--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  font-size: 9px;
  color: rgba(255, 255, 255, 0.45);
}

.plus-tv-square-card__meta-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.25;
  color: #fff;
}

.plus-tv-square-card__meta-subtitle {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.48);
}
</style>
